/**
 * BuildYourOwnCard - Step-by-step builder for custom items
 * Allows customers to build their own burger, breakfast, omelette, etc.
 */
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus, Check, ChevronRight, ChevronLeft, X, Utensils, AlertCircle, Heart, Bookmark } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import api from '../utils/apiClient';

export default function BuildYourOwnCard({ item, onClose, savedBuild = null }) {
  const [isExpanded, setIsExpanded] = useState(savedBuild ? true : false);
  const [currentStep, setCurrentStep] = useState(0);
  const [selections, setSelections] = useState({});
  const [specialNotes, setSpecialNotes] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [buildName, setBuildName] = useState('');
  const cardRef = useRef(null);
  
  const { addToCart } = useCart();
  const { isAuthenticated, user } = useAuth();

  // Initialize default selections when item loads
  useEffect(() => {
    if (savedBuild) {
      // Load saved build selections
      setSelections(savedBuild.selections || {});
      setSpecialNotes(savedBuild.special_instructions || '');
    } else if (item && item.sections) {
      const defaults = {};
      item.sections.forEach(section => {
        const defaultOptions = section.options
          .filter(opt => opt.is_default)
          .map(opt => ({ name: opt.name, price: opt.price, quantity: 1 }));
        if (defaultOptions.length > 0) {
          defaults[section.category] = defaultOptions;
        }
      });
      setSelections(defaults);
    }
  }, [item, savedBuild]);

  // Scroll card into view when expanded
  useEffect(() => {
    if (isExpanded && cardRef.current) {
      setTimeout(() => {
        cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    }
  }, [isExpanded]);

  const currentSection = item?.sections?.[currentStep];
  const totalSteps = item?.sections?.length || 0;

  // Check if current section meets minimum requirements
  const isSectionComplete = (section) => {
    const sectionSelections = selections[section.category] || [];
    return sectionSelections.length >= section.min_selections;
  };

  // Check if can proceed to next step
  const canProceed = currentSection ? isSectionComplete(currentSection) : false;

  // Calculate total price
  const calculateTotal = () => {
    let total = item?.base_price || 0;
    Object.values(selections).forEach(sectionSelections => {
      sectionSelections.forEach(sel => {
        total += (sel.price || 0) * (sel.quantity || 1);
      });
    });
    return Math.max(0, total);
  };

  // Toggle option selection
  const toggleOption = (section, option) => {
    setSelections(prev => {
      const current = prev[section.category] || [];
      const existingIdx = current.findIndex(s => s.name === option.name);
      
      if (existingIdx >= 0) {
        // Remove selection (only if min_selections allows it)
        if (section.min_selections > 0 && current.length <= section.min_selections) {
          // Can't remove - would go below minimum
          toast.error(`You must select at least ${section.min_selections} option(s)`);
          return prev;
        }
        return {
          ...prev,
          [section.category]: current.filter((_, i) => i !== existingIdx)
        };
      } else {
        // Add selection
        if (section.max_selections === 1) {
          // Single selection mode - replace existing selection
          return {
            ...prev,
            [section.category]: [{ name: option.name, price: option.price, quantity: 1 }]
          };
        } else if (current.length >= section.max_selections) {
          // Multi-selection mode - check max
          toast.error(`Maximum ${section.max_selections} selection(s) allowed`);
          return prev;
        }
        return {
          ...prev,
          [section.category]: [...current, { name: option.name, price: option.price, quantity: 1 }]
        };
      }
    });
  };

  // Check if option is selected
  const isSelected = (sectionCategory, optionName) => {
    const sectionSelections = selections[sectionCategory] || [];
    return sectionSelections.some(s => s.name === optionName);
  };

  // Get selection count for section
  const getSelectionCount = (sectionCategory) => {
    return (selections[sectionCategory] || []).length;
  };

  // Handle add to cart
  const handleAddToCart = async () => {
    // Validate all required sections
    let allComplete = true;
    item.sections.forEach(section => {
      if (!isSectionComplete(section)) {
        allComplete = false;
        toast.error(`Please complete: ${section.name}`);
      }
    });

    if (!allComplete) return;

    // Build the cart item
    const selectedOptions = [];
    Object.entries(selections).forEach(([category, items]) => {
      items.forEach(item => {
        selectedOptions.push(item.name);
      });
    });

    // Create a custom item for cart
    const customItem = {
      id: `${item.id}-${Date.now()}`,
      name: item.name,
      description: selectedOptions.join(', '),
      price: calculateTotal(),
      image: item.image,
      category: item.category,
      is_build_your_own: true,
      builder_id: item.id,
      selections: selections,
    };

    for (let i = 0; i < quantity; i++) {
      addToCart(
        customItem,
        null, // no size
        null, // no modifiers
        specialNotes,
        null, // no removed ingredients
        null, // no extra ingredients
        null  // no add-ons
      );
    }

    toast.success(`${quantity}x ${item.name} added to cart!`);
    
    // Record analytics
    try {
      await api.post('/build-analytics/record', {
        builder_id: item.id,
        selections: selections,
        special_instructions: specialNotes
      });
    } catch (err) {
      // Silent fail for analytics
    }
    
    resetAndClose();
  };

  // Save build for later
  const handleSaveBuild = async () => {
    if (!buildName.trim()) {
      toast.error('Please enter a name for your build');
      return;
    }
    
    try {
      const res = await api.post(`/saved-builds?user_id=${user?.id || user?.email}`, {
        builder_id: item.id,
        name: buildName.trim(),
        selections: selections,
        special_instructions: specialNotes
      });
      
      toast.success(res.data.message || 'Build saved!');
      setShowSaveDialog(false);
      setBuildName('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save build');
    }
  };

  // Reset and close
  const resetAndClose = () => {
    setIsExpanded(false);
    setCurrentStep(0);
    setSpecialNotes('');
    setQuantity(1);
    // Keep selections for next time
  };

  const totalPrice = calculateTotal();

  // Progress percentage
  const progress = totalSteps > 0 ? ((currentStep + 1) / totalSteps) * 100 : 0;

  return (
    <motion.div
      ref={cardRef}
      layout
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`build-your-own-card group bg-card rounded-3xl overflow-hidden shadow-lg border transition-all duration-300 ${
        isExpanded ? 'border-accent shadow-xl' : 'border-border hover:shadow-xl'
      }`}
      data-testid={`byo-item-${item.id}`}
    >
      {/* Collapsed View - Item Preview */}
      <div 
        className="cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="relative aspect-[4/3] overflow-hidden">
          <img 
            src={item.image} 
            alt={item.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <span className="absolute top-4 left-4 px-3 py-1 bg-accent text-white text-xs font-medium rounded-full flex items-center gap-1">
            <Utensils className="w-3 h-3" />
            Build Your Own
          </span>
          <span className="absolute bottom-4 right-4 px-3 py-1 bg-white/90 text-foreground text-sm font-medium rounded-full backdrop-blur-sm">
            From {item.base_price.toFixed(2)}
          </span>
        </div>
        
        <div className="p-4 sm:p-6">
          <h3 className="font-heading text-lg sm:text-xl mb-2">{item.name}</h3>
          <p className="text-xs sm:text-sm text-muted-foreground mb-4 line-clamp-2">
            {item.description}
          </p>
          
          <Button
            onClick={(e) => { e.stopPropagation(); setIsExpanded(true); }}
            className="w-full rounded-full bg-accent hover:bg-accent/90 text-white"
            data-testid={`start-building-${item.id}`}
          >
            <Utensils className="w-4 h-4 mr-2" />
            Start Building
          </Button>
        </div>
      </div>

      {/* Expanded View - Step by Step Builder */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="border-t border-border overflow-hidden"
          >
            <div className="p-4 sm:p-6 bg-muted/30">
              {/* Progress Bar */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium">
                    Step {currentStep + 1} of {totalSteps}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {totalPrice.toFixed(2)}
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-accent"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </div>

              {/* Current Section */}
              {currentSection && (
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="mb-4">
                    <h4 className="font-medium text-base sm:text-lg mb-1">
                      {currentSection.name}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      {currentSection.min_selections > 0 && `Choose at least ${currentSection.min_selections}`}
                      {currentSection.min_selections > 0 && currentSection.max_selections > currentSection.min_selections && ' • '}
                      {currentSection.max_selections > 1 && `Up to ${currentSection.max_selections} selections`}
                      {currentSection.min_selections === 0 && currentSection.max_selections > 1 && `Optional • Up to ${currentSection.max_selections}`}
                      {currentSection.min_selections === 0 && currentSection.max_selections === 1 && 'Optional'}
                    </p>
                  </div>

                  {/* Options Grid */}
                  <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                    {currentSection.options.map(option => {
                      const selected = isSelected(currentSection.category, option.name);
                      return (
                        <button
                          key={option.name}
                          onClick={() => toggleOption(currentSection, option)}
                          data-testid={`option-${option.name.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`w-full p-3 rounded-xl text-left transition-all border-2 flex items-center justify-between ${
                            selected
                              ? 'border-accent bg-accent/10'
                              : 'border-border bg-card hover:border-accent/50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                              selected ? 'border-accent bg-accent' : 'border-muted-foreground'
                            }`}>
                              {selected && <Check className="w-3 h-3 text-white" />}
                            </div>
                            <span className="font-medium text-sm">{option.name}</span>
                            {option.is_default && !selected && (
                              <span className="text-xs text-muted-foreground">(Recommended)</span>
                            )}
                          </div>
                          <span className={`text-sm font-medium ${
                            option.price > 0 ? 'text-accent' : 
                            option.price < 0 ? 'text-green-600' : 'text-muted-foreground'
                          }`}>
                            {option.price > 0 ? `+${option.price.toFixed(2)}` : 
                             option.price < 0 ? `-${Math.abs(option.price).toFixed(2)}` : 
                             'Included'}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Validation message */}
                  {currentSection.min_selections > 0 && !canProceed && (
                    <div className="mt-3 flex items-center gap-2 text-amber-600 dark:text-amber-400 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      Please select at least {currentSection.min_selections} option(s)
                    </div>
                  )}
                </motion.div>
              )}

              {/* Navigation */}
              <div className="mt-6 pt-4 border-t border-border">
                {currentStep < totalSteps - 1 ? (
                  <div className="flex items-center justify-between">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                      disabled={currentStep === 0}
                      className="rounded-full"
                      data-testid="prev-step"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Back
                    </Button>
                    <Button
                      onClick={() => setCurrentStep(currentStep + 1)}
                      disabled={!canProceed}
                      className="rounded-full bg-accent hover:bg-accent/90"
                      data-testid="next-step"
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Back Button */}
                    <Button
                      variant="outline"
                      onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                      className="rounded-full"
                      data-testid="prev-step"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Back
                    </Button>
                    
                    {/* Special Instructions */}
                    <div>
                      <label className="text-sm font-medium mb-2 block">Special Instructions (optional)</label>
                      <Textarea
                        value={specialNotes}
                        onChange={(e) => setSpecialNotes(e.target.value)}
                        placeholder="Any allergies or special requests..."
                        className="rounded-xl text-sm min-h-[60px]"
                        rows={2}
                        data-testid="byo-special-notes"
                      />
                    </div>
                    
                    {/* Quantity Controls - Centered */}
                    <div className="flex flex-col items-center gap-3">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setQuantity(Math.max(1, quantity - 1))}
                          className="w-10 h-10 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80"
                          data-testid="decrease-qty"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center font-medium text-lg">{quantity}</span>
                        <button
                          onClick={() => setQuantity(quantity + 1)}
                          className="w-10 h-10 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80"
                          data-testid="increase-qty"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      
                      {/* Add to Cart Button - Centered */}
                      <Button
                        onClick={handleAddToCart}
                        disabled={!canProceed}
                        className="w-full max-w-xs rounded-full bg-accent hover:bg-accent/90 h-12 text-base"
                        data-testid="add-to-cart-byo"
                      >
                        Add {(totalPrice * quantity).toFixed(2)}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Save Build Button (on last step) */}
              {currentStep === totalSteps - 1 && isAuthenticated && (
                <div className="mt-4">
                  <div className="flex items-center justify-between p-3 bg-primary/5 rounded-xl border border-primary/20">
                    {showSaveDialog ? (
                      <div className="flex items-center gap-2 w-full">
                        <Input
                          value={buildName}
                          onChange={(e) => setBuildName(e.target.value)}
                          placeholder="e.g., My Perfect Burger"
                          className="flex-1 h-9 text-sm"
                          data-testid="build-name-input"
                        />
                        <Button
                          size="sm"
                          onClick={handleSaveBuild}
                          className="rounded-full bg-primary hover:bg-primary/90 h-9"
                          data-testid="confirm-save-build"
                        >
                          <Bookmark className="w-4 h-4 mr-1" />
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { setShowSaveDialog(false); setBuildName(''); }}
                          className="h-9"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <Heart className="w-4 h-4 text-primary" />
                          <span className="text-sm">Love this combo?</span>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowSaveDialog(true)}
                          className="rounded-full border-primary/20 hover:border-primary h-8"
                          data-testid="save-build-btn"
                        >
                          <Bookmark className="w-4 h-4 mr-1" />
                          Save Build
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Close button */}
              <button
                onClick={resetAndClose}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-background/80 backdrop-blur flex items-center justify-center hover:bg-background transition-colors"
              >
                <X className="w-4 h-4" />
              </button>

              {/* Summary of selections */}
              {Object.keys(selections).length > 0 && (
                <div className="mt-4 p-3 bg-card rounded-xl border border-border">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Your selections:</p>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(selections).flatMap(([cat, items]) => 
                      items.map(item => (
                        <span
                          key={`${cat}-${item.name}`}
                          className="px-2 py-1 bg-orange-200 dark:bg-orange-800 text-orange-900 dark:text-orange-100 text-xs rounded-full font-medium"
                        >
                          {item.name}
                        </span>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
